package co.maytes.standupbot.dashboard.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.layout.MarketingShell
import co.maytes.standupbot.dashboard.components.marketing.Fold
import co.maytes.standupbot.dashboard.components.marketing.MkIcon
import co.maytes.standupbot.dashboard.components.marketing.PageIntro
import co.maytes.standupbot.dashboard.components.marketing.SectionHead
import co.maytes.standupbot.dashboard.components.motion.Reveal
import co.maytes.standupbot.dashboard.styles.Marketing
import co.maytes.standupbot.dashboard.styles.MarketingIcon
import co.maytes.standupbot.shared.billing.CompetitorPricing
import co.maytes.standupbot.shared.billing.PlanCatalog
import co.maytes.standupbot.shared.billing.PlanSpec
import co.maytes.standupbot.shared.marketing.Claims
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.classNames
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.navigation.Link
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Section
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul

private const val INSTALL_PATH = "/oauth/slack/install"
private const val CONTACT_PATH = "/contact"
private const val SLIDER_MIN_SEATS = 5
private const val SLIDER_MAX_SEATS = 100
private const val SLIDER_DEFAULT_SEATS = 20
private const val MONTHS_PER_YEAR = 12

@Page("/pricing")
@Composable
fun PricingPage() {
    MarketingShell {
        Div(attrs = { classes(Marketing.PAGE_CLASS) }) {
            PageIntro(
                title = "Unlimited seats. Forever.",
                line = "One flat price per workspace. Pro and Business bring their own Anthropic key.",
            )
            PlansSection()
            EnterpriseBand()
            SavingsCalculatorSection()
            FaqSection()
        }
    }
}

private data class Tier(
    val name: String,
    val price: String,
    val cadence: String,
    val monthlyNote: String?,
    val blurb: String,
    val features: List<String>,
    val plannedFeatures: List<String>,
    val ctaLabel: String,
    val ctaPath: String,
    val highlight: Boolean,
)

private fun PlanSpec.toTier(
    ctaLabel: String,
    ctaPath: String,
    highlight: Boolean = false,
): Tier {
    val free = monthlyPriceUsd == 0
    return Tier(
        name = displayName,
        price = if (free) "\$0" else "\$$annualMonthlyPriceUsd",
        cadence = if (free) "forever" else "per workspace each month, billed annually (\$$annualPriceUsd a year)",
        monthlyNote = if (free) null else "Or \$$monthlyPriceUsd a month, billed monthly",
        blurb = tagline,
        features = features,
        plannedFeatures = plannedFeatures,
        ctaLabel = ctaLabel,
        ctaPath = ctaPath,
        highlight = highlight,
    )
}

private val tiers: List<Tier> = listOf(
    PlanCatalog.free.toTier("Install to Slack", INSTALL_PATH),
    PlanCatalog.pro.toTier("Install to Slack", INSTALL_PATH, highlight = true),
    PlanCatalog.business.toTier("Contact us", CONTACT_PATH),
)

@Composable
private fun PlansSection() {
    Section(attrs = { classes("mk-section", "mk-section--tight") }) {
        Div(attrs = { classes("mk-wrap", "mk-plans") }) {
            tiers.forEachIndexed { index, tier ->
                Reveal(
                    index = index,
                    modifier = Modifier.classNames("mk-plan", if (tier.highlight) "mk-plan--pro" else "mk-plan--plain"),
                ) { PlanCard(tier) }
            }
        }
    }
}

@Composable
private fun PlanCard(tier: Tier) {
    if (tier.highlight) Span(attrs = { classes("mk-plan-flag") }) { Text("Most popular") }
    H3(attrs = { classes("mk-plan-name") }) { Text(tier.name) }
    P(attrs = { classes("mk-body") }) { Text(tier.blurb) }
    Div(attrs = { classes("mk-price-block") }) {
        Span(attrs = { classes("mk-price") }) { Text(tier.price) }
        Span(attrs = { classes("mk-price-cadence") }) { Text(tier.cadence) }
        tier.monthlyNote?.let { Span(attrs = { classes("mk-price-note") }) { Text(it) } }
    }
    Ul(attrs = { classes("mk-feats") }) {
        tier.features.forEach { FeatureRow(it, planned = false) }
        tier.plannedFeatures.forEach { FeatureRow("$it (planned)", planned = true) }
    }
    Link(
        path = tier.ctaPath,
        modifier = Modifier.classNames("mk-btn", if (tier.highlight) "mk-btn--primary" else "mk-btn--ghost", "mk-btn--block"),
    ) { Text(tier.ctaLabel) }
}

@Composable
private fun FeatureRow(text: String, planned: Boolean) {
    Li(attrs = { classes("mk-feat"); if (planned) classes("mk-feat--planned") }) {
        Span(attrs = { classes("mk-feat-icon") }) {
            MkIcon(if (planned) MarketingIcon.Clock else MarketingIcon.Check)
        }
        Span { Text(text) }
    }
}

@Composable
private fun EnterpriseBand() {
    Section(attrs = { classes("mk-section", "mk-section--tight") }) {
        Div(attrs = { classes("mk-wrap") }) {
            Reveal(modifier = Modifier.classNames("mk-callout")) {
                Div(attrs = { classes("mk-callout-copy") }) {
                    H3(attrs = { classes("mk-plan-name") }) { Text("Need SSO, a DPA, or your own hosting?") }
                    P(attrs = { classes("mk-body") }) {
                        Text("We offer self hosting, a custom contract and tailored support.")
                    }
                }
                Link(
                    path = CONTACT_PATH,
                    modifier = Modifier.classNames("mk-btn", "mk-btn--primary"),
                ) { Text("Talk to sales") }
            }
        }
    }
}

@Composable
private fun SavingsCalculatorSection() {
    var seats by remember { mutableStateOf(SLIDER_DEFAULT_SEATS) }
    val pro = PlanCatalog.pro
    val tiered = CompetitorPricing.tiered
    val geekbotDollars = CompetitorPricing.geekbot.monthlyDollars(seats)
    val incumbentDollars = tiered.monthlyDollars(seats)
    val barMax = maxOf(
        geekbotDollars,
        CompetitorPricing.dailyBot.monthlyDollars(seats),
        CompetitorPricing.range.monthlyDollars(seats),
        incumbentDollars,
        pro.monthlyPriceUsd,
        pro.annualMonthlyPriceUsd,
    )
    val geekbotYearlyDollars = geekbotDollars * MONTHS_PER_YEAR
    val annualPlanSavings = (geekbotYearlyDollars - pro.annualPriceUsd).coerceAtLeast(0)
    val monthlyPlanSavings = (geekbotYearlyDollars - pro.monthlyPriceUsd * MONTHS_PER_YEAR).coerceAtLeast(0)

    Section(attrs = { classes("mk-section", "mk-band") }) {
        Div(attrs = { classes("mk-wrap", "mk-calc") }) {
            SectionHead(
                title = "Rivals bill headcount. We don't.",
                line = "Slide to your team size.",
            )
            Reveal(modifier = Modifier.classNames("mk-calc-card")) {
                Div(attrs = { classes("mk-seat-head") }) {
                    Span(attrs = { classes("mk-seat-label") }) { Text("Team size") }
                    Span(attrs = { classes("mk-seat-count") }) { Text("$seats ${if (seats == 1) "seat" else "seats"}") }
                }
                Input(
                    type = InputType.Range,
                    attrs = Modifier.toAttrs {
                        classes("mk-slider")
                        attr("min", SLIDER_MIN_SEATS.toString())
                        attr("max", SLIDER_MAX_SEATS.toString())
                        attr("step", "1")
                        value(seats)
                        attr("aria-label", "Team size in seats")
                        onInput { e -> e.value?.toInt()?.let { seats = it } }
                    },
                )
                Div(attrs = { classes("mk-seat-ends") }) {
                    Span { Text("$SLIDER_MIN_SEATS") }
                    Span { Text("$SLIDER_MAX_SEATS+") }
                }
                Div(attrs = { classes("mk-bars-v") }) {
                    PriceBar("StandupBot Pro, billed annually", pro.annualMonthlyPriceUsd, barMax, "flat for any team size", true)
                    PriceBar("StandupBot Pro, billed monthly", pro.monthlyPriceUsd, barMax, "flat for any team size", true)
                    CompetitorPricing.perSeatPlans.forEach { plan ->
                        PriceBar(
                            "${plan.product} (${plan.perSeatLabel} a seat)",
                            plan.monthlyDollars(seats),
                            barMax,
                            "$seats seats at ${plan.perSeatLabel} each",
                            false,
                        )
                    }
                    PriceBar("${tiered.product} (tiered)", incumbentDollars, barMax, tiered.tierNote(seats), false)
                }
                if (annualPlanSavings > 0) {
                    Div(attrs = { classes("mk-saving") }) {
                        MkIcon(MarketingIcon.Check)
                        Span { Text(savingsHeadline(seats, annualPlanSavings, monthlyPlanSavings)) }
                    }
                }
                CalculatorFootnote()
            }
        }
    }
}

private fun savingsHeadline(seats: Int, annualPlanSavings: Int, monthlyPlanSavings: Int): String {
    val base = "You would save \$$annualPlanSavings a year compared with Geekbot at $seats seats, on Pro billed annually"
    return if (monthlyPlanSavings > 0) "$base. On monthly billing you would save \$$monthlyPlanSavings." else "$base."
}

private fun displayHost(url: String): String = url.removePrefix("https://").removePrefix("www.")

@Composable
private fun CalculatorFootnote() {
    Div(attrs = { classes("mk-foot") }) {
        Span { Text("Prices as of ${CompetitorPricing.asOf}") }
        Div(attrs = { classes("mk-foot-links") }) {
            Span { Text("Sources:") }
            CompetitorPricing.sources.forEach { url ->
                Link(path = url, modifier = Modifier.classNames("mk-foot-link")) { Text(displayHost(url)) }
            }
        }
        P(attrs = { classes("mk-foot-note") }) {
            Text(
                "StandupBot's price does not include the AI. You pay Anthropic directly for your own key, " +
                    "typically ${Claims.TYPICAL_ANTHROPIC_SPEND}.",
            )
        }
    }
}

@Composable
private fun PriceBar(label: String, monthlyDollars: Int, relativeTo: Int, seatsNote: String, accent: Boolean) {
    val widthPercent = if (relativeTo <= 0) 0 else (monthlyDollars * 100) / relativeTo
    Div(attrs = { classes("mk-pbar"); if (accent) classes("mk-pbar--accent") }) {
        Div(attrs = { classes("mk-pbar-head") }) {
            Span(attrs = { classes("mk-pbar-label") }) { Text(label) }
            Span(attrs = { classes("mk-pbar-price") }) {
                Text("\$$monthlyDollars")
                Span(attrs = { classes("mk-pbar-note") }) { Text(" a month, $seatsNote") }
            }
        }
        Div(attrs = { classes("mk-pbar-track") }) {
            Span(attrs = { classes("mk-pbar-fill"); style { property("--w", "$widthPercent") } })
        }
    }
}

private data class FaqEntry(val question: String, val answer: String)

private val faqs: List<FaqEntry> = listOf(
    FaqEntry(
        question = "Why price per workspace instead of per seat?",
        answer = "Our marginal cost does not scale with your team size. On paid plans the AI runs on your own " +
            "Anthropic key, and our infrastructure is identical whether you have 5 people or 500. " +
            "Per seat pricing bills you for hiring. We have no reason to.",
    ),
    FaqEntry(
        question = "What does bring your own Anthropic key mean?",
        answer = "You create an Anthropic account, generate an API key, and paste it into the StandupBot " +
            "dashboard. From then on your issue titles, PR descriptions and commit activity are processed " +
            "under your Anthropic account, with your commercial terms and your retention policy, and " +
            "revocable the moment you rotate the key. We never hold your work in our own model account. " +
            "The AI spend is between you and Anthropic, typically ${Claims.TYPICAL_ANTHROPIC_SPEND}. " +
            "A full walkthrough lives at /docs/anthropic-key.",
    ),
    FaqEntry(
        question = "How do we compare to Geekbot, DailyBot or StandupBot.com?",
        answer = "Every one of them DMs your teammates a questionnaire each morning and waits for humans to " +
            "answer. We read Linear and GitHub and write the standup for you, so nobody types a word. " +
            "Because we hold structured delivery history rather than self reported prose, we can also " +
            "produce accomplishment summaries for retros and reviews. Try the slider above for the " +
            "price comparison.",
    ),
    FaqEntry(
        question = "What counts as a member?",
        answer = "Anyone on the standup's roster who has their Linear identity linked. Someone who is in the " +
            "Slack channel but not on the roster does not count. The Free cap of " +
            "${PlanCatalog.free.memberCap} lets small teams evaluate without a card. Past that you will " +
            "want Pro.",
    ),
    FaqEntry(
        question = "Can I host it myself?",
        answer = "On Enterprise, yes. Pro and Business run in our cloud on Fly.io and Supabase. Tokens are " +
            "encrypted at rest with AES-GCM (AES-256, with a keyring per workspace and rotation support). " +
            "Email us for an Enterprise quote.",
    ),
    FaqEntry(
        question = "What happens when I pass a Free limit?",
        answer = "Your existing standups keep running. Going past a Free limit, such as more than " +
            "${PlanCatalog.free.memberCap} members or a second standup, does not switch anything off. " +
            "It is a reason to move to Pro. We will not drop a standup mid sprint because you hit the " +
            "member cap overnight.",
    ),
    FaqEntry(
        question = "Do I have to bring my own Anthropic key?",
        answer = "On Pro and Business, yes. It is what makes unlimited seats honest and keeps your work out " +
            "of our model account. The Free plan runs on our key with caps, so you can evaluate without " +
            "creating an Anthropic account first. If a paid workspace has no key, AI summaries stop and " +
            "the standup posts raw activity until the workspace adds one. We never fall back to ours.",
    ),
    FaqEntry(
        question = "Do you offer annual discounts?",
        answer = "Yes. Pro annual is \$${PlanCatalog.pro.annualPriceUsd} a year " +
            "(\$${PlanCatalog.pro.annualMonthlyPriceUsd} a month effective, saving " +
            "\$${PlanCatalog.pro.annualSavingsUsd}). Business annual is " +
            "\$${PlanCatalog.business.annualPriceUsd} a year " +
            "(\$${PlanCatalog.business.annualMonthlyPriceUsd} a month effective, saving " +
            "\$${PlanCatalog.business.annualSavingsUsd}). Early stage startups can contact us for " +
            "first year discount pricing.",
    ),
    FaqEntry(
        question = "Can I cancel anytime?",
        answer = "Any time, from the billing page in the dashboard, which opens a Stripe customer portal. " +
            "You keep access until the end of the current period, then move to Free.",
    ),
)

@Composable
private fun FaqSection() {
    Section(attrs = { classes("mk-section") }) {
        Div(attrs = { classes("mk-wrap", "mk-narrow") }) {
            SectionHead(title = "Questions we keep getting.", line = "Tap a question to open it.")
            Div(attrs = { classes("mk-faq") }) {
                faqs.forEachIndexed { index, entry ->
                    Reveal(index = index) {
                        Fold(title = entry.question) { P(attrs = { classes("mk-body") }) { Text(entry.answer) } }
                    }
                }
            }
        }
    }
}
